<script lang="ts">
  import { onMount } from "svelte";
  import { FileItem } from "../models/state";
  import { FileBrowserProps } from "../models/props";
  import { appStore } from "../stores/appStore";
  import type { BranchInfo } from "../api_contracts/app";

  let {
    branch,
    lastCommit,
    files,
    currentPath = [],
    onFileClick,
    onNavigateUp,
    onNavigateToPath,
    ownerId,
    repositoryName,
  }: FileBrowserProps = $props();

  let showBranchMenu = $state(false);
  let showCodeMenu = $state(false);
  let selectedCloneMethod = $state<"https" | "ssh" | "cli">("https");
  let branchSearchQuery = $state("");
  let branches = $state<BranchInfo[]>([]);
  let defaultBranch = $state("");
  let loadingBranches = $state(false);
  let profile: UserProfile | null = $state(null);
  let client = $state(null);
  let token = $state("");

  // Get the repository key for caching
  const repoKey = $derived(`${ownerId}/${repositoryName}`);

  // Subscribe to store for client and token
  appStore.subscribe((state) => {
    client = state.client;
    token = state.token;
    profile = state.profile;
  });

  // Fetch branches from API if not in store
  async function fetchBranches() {
    if (!client || !token) {
      console.error("Client or token not available");
      return;
    }

    // Check if branches exist in global state
    const cached = appStore.getBranches(repoKey);
    if (cached) {
      branches = cached.branches;
      defaultBranch = cached.defaultBranch;
      return;
    }

    // Fetch from API
    try {
      loadingBranches = true;
      const result = await client.getBranches({
        token,
        ownerId,
        repositoryName,
      });

      if (result.response.successFull) {
        branches = result.response.branches;
        defaultBranch = result.response.defaultBranch;

        // Store in global state
        appStore.setBranches(repoKey, branches, defaultBranch);
      } else {
        console.error("Failed to fetch branches:", result.response.reason);
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    } finally {
      loadingBranches = false;
    }
  }

  onMount(() => {
    fetchBranches();
  });

  // Helper to format commit date
  function formatCommitDate(date?: {
    seconds: bigint | number | string;
  }): string {
    if (!date || !date.seconds) return "Unknown";

    // Convert BigInt or string to number
    let timestampNum: number;
    if (typeof date.seconds === "bigint") {
      timestampNum = Number(date.seconds);
    } else if (typeof date.seconds === "string") {
      timestampNum = parseInt(date.seconds, 10);
    } else {
      timestampNum = date.seconds;
    }

    // Check if timestamp is in seconds or milliseconds
    // If the number is larger than year 3000 in seconds (32503680000), it's likely in milliseconds
    let commitDate: Date;
    if (timestampNum > 32503680000) {
      // Already in milliseconds
      commitDate = new Date(timestampNum);
    } else {
      // In seconds, convert to milliseconds
      commitDate = new Date(timestampNum * 1000);
    }

    const now = new Date();
    const diffMs = now.getTime() - commitDate.getTime();

    // Handle negative differences (future dates) or invalid dates
    if (diffMs < 0 || isNaN(diffMs)) {
      return "Unknown";
    }

    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    const diffWeeks = Math.floor(diffMs / 604800000);
    const diffMonths = Math.floor(diffMs / 2592000000);
    const diffYears = Math.floor(diffMs / 31536000000);

    if (diffMins < 1) return "just now";
    if (diffMins < 60)
      return `${diffMins} minute${diffMins !== 1 ? "s" : ""} ago`;
    if (diffHours < 24)
      return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
    if (diffWeeks < 4)
      return `${diffWeeks} week${diffWeeks !== 1 ? "s" : ""} ago`;
    if (diffMonths < 12)
      return `${diffMonths} month${diffMonths !== 1 ? "s" : ""} ago`;
    return `${diffYears} year${diffYears !== 1 ? "s" : ""} ago`;
  }

  const filteredBranches = $derived(
    branches.filter((b) =>
      b.name.toLowerCase().includes(branchSearchQuery.toLowerCase()),
    ),
  );

  // Mock repository URLs
  const repoUrls = {
    https: "https://github.com/user/repository.git",
    ssh: "git@github.com:user/repository.git",
    cli: "gh repo clone user/repository",
  };

  function copyToClipboard(text: string) {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        console.log("Copied to clipboard:", text);
      })
      .catch((err) => {
        console.error("Failed to copy:", err);
      });
  }

  const handleFileClick = (file: FileItem, event: MouseEvent) => {
    event.preventDefault();
    if (onFileClick) {
      onFileClick(file);
    }
  };

  const handleParentClick = (event: MouseEvent) => {
    event.preventDefault();
    if (onNavigateUp) {
      onNavigateUp();
    }
  };

  const handlePathClick = (index: number, event: MouseEvent) => {
    event.preventDefault();
    if (onNavigateToPath) {
      onNavigateToPath(index);
    }
  };
</script>

<div class="mb-8">
  <!-- Back Button (show when in a folder) -->
  {#if currentPath.length > 0}
    <div class="mb-4">
      <button
        onclick={handleParentClick}
        class="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
      >
        <svg
          class="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M10 19l-7-7m0 0l7-7m-7 7h18"
          />
        </svg>
        <span class="font-medium">Back</span>
      </button>
    </div>
  {/if}

  <!-- Breadcrumb and Actions Bar -->
  <div class="flex justify-between items-center mb-4">
    <div class="flex items-center space-x-2 text-sm">
      <!-- <button class="flex items-center px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded border border-gray-300 transition-colors font-medium">
				<svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
					<path fill-rule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd"/>
				</svg>
				{branch}
			</button> -->

      <div class="relative">
        <button
          onclick={() => (showBranchMenu = !showBranchMenu)}
          class="flex items-center px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded border border-gray-300 text-xs transition-colors"
        >
          <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
          {branch}
        </button>

        {#if showBranchMenu}
          <div
            class="absolute left-0 mt-2 w-80 rounded-lg shadow-xl bg-white border border-gray-200 z-20 overflow-hidden"
          >
            <!-- Header -->
            <div class="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <h3 class="text-sm font-semibold text-gray-900">
                Switch branches/tags
              </h3>
            </div>

            <!-- Search -->
            <div class="px-4 py-3 border-b border-gray-200">
              <div class="relative">
                <input
                  type="text"
                  bind:value={branchSearchQuery}
                  placeholder="Find a branch..."
                  class="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white"
                />
                <svg
                  class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>

            <!-- Branches List -->
            <div class="max-h-80 overflow-y-auto">
              <div class="px-3 py-2">
                <div class="text-xs font-semibold text-gray-500 px-3 py-1">
                  Branches
                </div>

                {#if loadingBranches}
                  <div class="px-3 py-4 text-sm text-gray-500 text-center">
                    Loading branches...
                  </div>
                {:else if filteredBranches.length === 0}
                  <div class="px-3 py-4 text-sm text-gray-500 text-center">
                    No branches found
                  </div>
                {:else}
                  {#each filteredBranches as branchItem}
                    <div
                      onclick={() => {
                        console.log("Switch to branch:", branchItem.name);
                        showBranchMenu = false;
                      }}
                      class="w-full flex items-start gap-3 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors {branchItem.name ===
                      branch
                        ? 'bg-blue-50 dark:bg-blue-900'
                        : ''}"
                    >
                      <svg
                        class="w-4 h-4 text-gray-500 dark:text-gray-400 mt-0.5 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M7.707 3.293a1 1 0 010 1.414L5.414 7H11a7 7 0 017 7v2a1 1 0 11-2 0v-2a5 5 0 00-5-5H5.414l2.293 2.293a1 1 0 11-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                          clip-rule="evenodd"
                        />
                      </svg>
                      <div class="flex-1 text-left">
                        <div class="flex items-center gap-2">
                          <span
                            class="font-medium text-gray-900 dark:text-gray-100 {branchItem.name ===
                            branch
                              ? 'text-blue-700 dark:text-blue-300'
                              : ''}">{branchItem.name}</span
                          >
                          {#if branchItem.isDefault}
                            <span
                              class="px-1.5 py-0.5 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 rounded border border-green-200 dark:border-green-700"
                            >
                              default
                            </span>
                          {/if}
                          {#if branchItem.name === branch}
                            <svg
                              class="w-4 h-4 text-blue-600 dark:text-blue-400"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fill-rule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clip-rule="evenodd"
                              />
                            </svg>
                          {/if}
                        </div>
                        <div
                          class="text-xs text-gray-500 dark:text-gray-400 mt-0.5"
                        >
                          Updated {formatCommitDate(branchItem.commitDate)}
                        </div>
                      </div>
                    </div>
                  {/each}
                {/if}
              </div>
            </div>

            <!-- Footer -->
            <div
              class="px-4 py-3 bg-gray-50 border-t border-gray-200 text-center"
            >
              <button
                onclick={() => {
                  console.log("View all branches");
                  showBranchMenu = false;
                  // Extract username from current URL
                  const hash = window.location.hash;
                  const match = hash.match(/\/users\/([^\/]+)\//);
                  const username = match ? match[1] : profile?.userName;
                  window.location.hash = `/users/${username}/repositories/${repositoryName}/branches`;
                }}
                class="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                View all branches
              </button>
            </div>
          </div>
          <div
            class="fixed inset-0 z-10"
            onclick={() => (showBranchMenu = false)}
          ></div>
        {/if}
      </div>

      <span class="text-gray-400">/</span>

      <!-- Breadcrumb Path -->
      {#if currentPath.length > 0}
        {#each currentPath as folder, index}
          <button
            onclick={(e) => handlePathClick(index, e)}
            class="text-blue-600 hover:underline font-medium"
          >
            {folder}
          </button>
          <span class="text-gray-400">/</span>
        {/each}
      {/if}
    </div>

    <div class="flex items-center space-x-2">
      <!-- <button class="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded border border-gray-300 text-sm transition-colors font-medium">
				Add file
			</button> -->

      <div class="relative">
        <button
          onclick={() => (showCodeMenu = !showCodeMenu)}
          class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-md transition-all shadow-sm flex items-center gap-2"
        >
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M12.316 3.051a1 1 0 01.633 1.09l-1.583 8.32a1 1 0 01-1.077.832l-7-2a1 1 0 01-.633-1.09l1.583-8.32a1 1 0 011.077-.832l7 2zM4.09 10.982L2.5 10.5l-.5.25a.5.5 0 00-.25.5l.25.5.5-.25 1.583.316a1 1 0 01-.633 1.09L2.5 13.5l.5-.25a.5.5 0 00.25-.5l-.25-.5-.5.25zM15 11a1 1 0 100-2 1 1 0 000 2z"
              clip-rule="evenodd"
            />
          </svg>
          Code
          <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>

        {#if showCodeMenu}
          <div
            class="absolute right-0 mt-2 w-96 rounded-lg shadow-xl bg-white border border-gray-200 z-20 overflow-hidden"
          >
            <!-- Header -->
            <div class="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <h3 class="text-sm font-semibold text-gray-900">Clone</h3>
            </div>

            <!-- Clone Methods Tabs -->
            <div class="px-4 py-3 border-b border-gray-200">
              <div class="flex gap-2">
                <button
                  onclick={() => (selectedCloneMethod = "https")}
                  class="px-3 py-1.5 text-xs font-medium rounded-md transition-colors {selectedCloneMethod ===
                  'https'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-100'}"
                >
                  HTTPS
                </button>
                <button
                  onclick={() => (selectedCloneMethod = "ssh")}
                  class="px-3 py-1.5 text-xs font-medium rounded-md transition-colors {selectedCloneMethod ===
                  'ssh'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-100'}"
                >
                  SSH
                </button>
                <!-- <button
                                    onclick={() => selectedCloneMethod = 'cli'}
                                    class="px-3 py-1.5 text-xs font-medium rounded-md transition-colors {selectedCloneMethod === 'cli' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'}"
                                >
                                    GitHub CLI
                                </button> -->
              </div>

              <!-- Clone URL Input -->
              <div class="mt-3 relative">
                <input
                  type="text"
                  value={repoUrls[selectedCloneMethod]}
                  readonly
                  class="w-full pl-3 pr-10 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm font-mono text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onclick={() => copyToClipboard(repoUrls[selectedCloneMethod])}
                  class="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 hover:bg-gray-200 rounded transition-colors"
                  title="Copy to clipboard"
                >
                  <svg
                    class="w-4 h-4 text-gray-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                    <path
                      d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z"
                    />
                  </svg>
                </button>
              </div>

              {#if selectedCloneMethod === "https"}
                <p class="mt-2 text-xs text-gray-500">
                  Use Git or checkout with SVN using the web URL.
                </p>
              {:else if selectedCloneMethod === "ssh"}
                <p class="mt-2 text-xs text-gray-500">
                  Use a password protected SSH key.
                </p>
              {:else if selectedCloneMethod === "cli"}
                <p class="mt-2 text-xs text-gray-500">
                  Work fast with our official CLI. <a
                    href="#"
                    class="text-blue-600 hover:underline">Learn more</a
                  >
                </p>
              {/if}
            </div>

            <!-- Open with Options -->
            <div class="px-4 py-3 border-b border-gray-200">
              <div class="text-xs font-semibold text-gray-500 mb-2">
                Open with
              </div>
              <div class="space-y-1">
                <button
                  onclick={() => {
                    console.log("Open with GitHub Desktop");
                    showCodeMenu = false;
                  }}
                  class="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                >
                  <svg
                    class="w-5 h-5 text-gray-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M3 5a2 2 0 012-2h10a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V5zm11 1H6v8l4-2 4 2V6z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <span class="font-medium">Open with Online Editor</span>
                </button>
                <button
                  onclick={() => {
                    console.log("Open with Visual Studio Code");
                    showCodeMenu = false;
                  }}
                  class="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                >
                  <svg
                    class="w-5 h-5 text-blue-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"
                    />
                  </svg>
                  <span class="font-medium">Open with Visual Studio Code</span>
                </button>
              </div>
            </div>

            <!-- Download Options -->
            <div class="px-4 py-3">
              <button
                onclick={() => {
                  console.log("Download ZIP");
                  showCodeMenu = false;
                }}
                class="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
              >
                <svg
                  class="w-5 h-5 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                <span class="font-medium">Download ZIP</span>
              </button>
            </div>
          </div>
          <div
            class="fixed inset-0 z-10"
            onclick={() => (showCodeMenu = false)}
          ></div>
        {/if}
      </div>
    </div>
  </div>

  <!-- File List Container -->
  <div class="border border-gray-300 rounded-md overflow-hidden bg-white">
    <!-- Latest Commit Info Bar -->
    <div
      class="px-4 py-2.5 bg-gray-50 border-b border-gray-300 flex justify-between items-center"
    >
      <div class="flex items-center space-x-3">
        <!-- Avatar -->

        {#if profile && profile.avatarUrl}
          <img
            src={profile.avatarUrl}
            alt="User avatar"
            class="w-8 h-8 rounded-full object-cover"
          />
        {:else if profile && profile.firstName && profile.lastName}
          <div
            class="w-8 h-8 rounded-full bg-indigo-600 dark:bg-indigo-500 flex items-center justify-center text-white text-sm font-semibold"
          >
            {profile.firstName.charAt(0)}{profile.lastName.charAt(0)}
          </div>
        {/if}

        <!-- Commit Info -->
        <div class="flex items-center space-x-2 text-sm">
          <a href="#" class="text-blue-600 hover:underline font-semibold"
            >{lastCommit.author}</a
          >
          <span class="text-gray-700">{lastCommit.message}</span>
        </div>
      </div>
      <div class="flex items-center space-x-3 text-sm text-gray-600">
        <a href="#" class="hover:text-blue-600 hover:underline font-mono"
          >{lastCommit.sha}</a
        >
        <span>•</span>
        <span>{lastCommit.time}</span>
        <button
          class="px-3 py-1 bg-white hover:bg-gray-100 border border-gray-300 text-gray-700 rounded text-sm transition-colors flex items-center space-x-1 font-medium"
        >
          <svg
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>History</span>
        </button>
      </div>
    </div>

    <!-- File Table -->
    <div class="divide-y divide-gray-200">
      <!-- Table Header -->
      <div
        class="grid grid-cols-12 px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-600"
      >
        <div class="col-span-5">Name</div>
        <div class="col-span-5">Last commit message</div>
        <div class="col-span-2 text-right">Last commit date</div>
      </div>

      <!-- Parent Directory (only show if we're in a subfolder) -->
      {#if currentPath.length > 0}
        <div
          class="grid grid-cols-12 px-4 py-2 hover:bg-gray-50 transition-colors"
        >
          <div class="col-span-5 flex items-center space-x-2">
            <svg
              class="w-4 h-4 text-gray-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"
              />
            </svg>
            <button
              onclick={handleParentClick}
              class="text-blue-600 hover:underline text-sm font-medium"
            >
              ..
            </button>
          </div>
          <div class="col-span-5"></div>
          <div class="col-span-2"></div>
        </div>
      {/if}

      <!-- File Rows -->
      {#each files as file}
        <div
          class="grid grid-cols-12 px-4 py-2 hover:bg-gray-50 transition-colors"
        >
          <div class="col-span-5 flex items-center space-x-2">
            {#if file.type === "folder"}
              <svg
                class="w-4 h-4 text-blue-600 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"
                />
              </svg>
            {:else}
              <svg
                class="w-4 h-4 text-gray-600 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              > 
                <path
                  fill-rule="evenodd"
                  d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                  clip-rule="evenodd"
                />
              </svg>
            {/if}
            <div
              onclick={(e) => handleFileClick(file, e)}
              class="text-blue-600 hover:underline text-sm truncate text-left font-medium"
            >
              {file.name}
            </div>
          </div>
          <div class="col-span-5 text-sm text-gray-600 truncate px-2">
            {file.message}
          </div>
          <div class="col-span-2 text-right text-sm text-gray-600">
            {file.updatedAt}
          </div>
        </div>
      {/each}
    </div>
  </div>
</div>
